'use client'

import Container from '#/components/layout/Container'
import Section from '#/components/layout/Section'
import CategoryTabs from '#/components/menu/CategoryTabs'
import MenuGrid from '#/components/menu/MenuGrid'
import SeasonalSection from '#/components/menu/SeasonalSection'
import WeeklyCalendar from '#/components/menu/WeeklyCalendar'
import { Tabs, TabsContent } from '#/components/ui/tabs'
import { useCmsContent, useCmsObjectContent } from '#/hooks/useCmsContent'
import EditablePageHeader from '#/components/cms/EditablePageHeader'
import OrderOnlineButton from '#/components/layout/OrderOnlineButton'
import { getHours, getMenuCategories, getMenuItems, getSiteConfig } from '#/lib/content'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { canonicalLink, seoMeta } from '#/lib/seo'

export const Route = createFileRoute('/menu')({
  head: () => ({
    meta: seoMeta({
      title: 'Menu',
      description:
        'Fresh bread and pastries baked daily at Main Street Bread in Ferndale. Check our weekly schedule and seasonal specials.',
      path: '/menu',
    }),
    links: [canonicalLink('/menu')],
  }),
  loader: async () => {
    const [items, categories, hours, config] = await Promise.all([
      getMenuItems(),
      getMenuCategories(),
      getHours(),
      getSiteConfig(),
    ])
    return { items, categories, hours, config }
  },
  component: MenuPage,
})

function MenuPage() {
  const data = Route.useLoaderData()
  const items = useCmsContent('menu-items', data.items)
  const userCategories = useCmsContent('menu-categories', data.categories)
  const hours = useCmsContent('hours', data.hours)
  const config = useCmsObjectContent('site-config', data.config)
  const [activeCategory, setActiveCategory] = useState('all')

  const seasonalItems = items.filter((item) => item.seasonal)

  const allTabs = useMemo(
    () => [
      { value: 'all', label: 'All' },
      ...userCategories.map((c) => ({ value: c.id, label: c.label })),
      { value: 'seasonal', label: 'Seasonal' },
    ],
    [userCategories],
  )

  return (
    <main>
      {/* Header */}
      <Section tone="surface" className="py-8 sm:py-12 lg:py-14 ">
        <Container>
          <EditablePageHeader page="menu" />
          {config.orderOnlineUrl && (
            <div className="mt-8 flex flex-col items-center gap-2">
              <OrderOnlineButton url={config.orderOnlineUrl} />
              <p className="text-sm text-on-surface-variant">Order ahead for pickup</p>
            </div>
          )}
        </Container>
      </Section>

      {/* Category Tabs + Grid */}
      <Section tone="surface">
        <Container>
          <Tabs value={activeCategory} onValueChange={setActiveCategory}>
            <div className="mb-10 flex justify-center">
              <CategoryTabs
                allTabs={allTabs}
                userCategories={userCategories}
                items={items}
              />
            </div>

            {allTabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                <MenuGrid
                  items={items}
                  category={tab.value}
                  userCategories={userCategories}
                />
              </TabsContent>
            ))}
          </Tabs>
        </Container>
      </Section>

      {/* Seasonal Specials */}
      <Section tone="low">
        <Container>
          <div className="mb-10 text-center sm:mb-14">
            <p className="kicker mb-2">Limited Time</p>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-on-surface sm:text-4xl">
              Seasonal Specials
            </h2>
          </div>
          <SeasonalSection items={seasonalItems} />
        </Container>
      </Section>

      {/* Weekly Calendar */}
      <Section tone="low" className="py-8 sm:py-12">
        <Container>
          <h2 className="mb-6 text-center font-serif text-2xl font-bold text-on-surface sm:text-3xl">
            This Week&apos;s Schedule
          </h2>
          <WeeklyCalendar items={items} hours={hours} />
        </Container>
      </Section>


    </main>
  )
}
